import React, { useState, useEffect, useMemo } from 'react';
import { 
  CogIcon, 
  PlusIcon, 
  TrashIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation } from '../../hooks/useApi';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface MasterData {
  id: string;
  category: string;
  name: string;
  description?: string;
  unit?: string;
  market_price_per_kg?: number;
  is_active: boolean;
  created_at: string;
}

interface MasterDataFormData {
  name: string;
  description: string;
  unit: string;
  market_price_per_kg?: number;
}

const masterDataSchema = yup.object({
  name: yup.string().required('Name is required'),
  description: yup.string(),
  unit: yup.string(),
  market_price_per_kg: yup.number().min(0, 'Price must be positive').nullable(),
});

const categories = [
  { id: 'fish_species', name: 'Fish Species', icon: '🐟', description: 'Types of fish for farming' },
  { id: 'medicine_types', name: 'Fish Medicine', icon: '💊', description: 'Fish medicines and treatments' },
  { id: 'feed_brands', name: 'Fish Feed', icon: '🌾', description: 'Fish feed brands and types' },
  { id: 'goat_feed_brands', name: 'Goat Feed', icon: '🌾', description: 'Goat feed brands and types' },
  { id: 'goat_medicine_types', name: 'Goat Medicine', icon: '💊', description: 'Goat medicines and treatments' },
  { id: 'goat_breeds', name: 'Goat Breed', icon: '🐐', description: 'Goat breeds for farming' },
  { id: 'other_items', name: 'Other Items', icon: '📦', description: 'Consumables like diesel, salt, etc.' },
];

const fishCategoryIds = new Set(['fish_species', 'medicine_types', 'feed_brands']);
const goatCategoryIds = new Set(['goat_feed_brands', 'goat_medicine_types', 'goat_breeds']);

const MasterDataManagement: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [selectedCategory, setSelectedCategory] = useState('fish_species');
  const [masterData, setMasterData] = useState<MasterData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingItem, setEditingItem] = useState<MasterData | null>(null);
  const [error, setError] = useState('');

  const { mutate: createMasterData, loading: creating } = useMutation<MasterData, any>();
  
  const form = useForm<MasterDataFormData>({
    resolver: yupResolver(masterDataSchema) as any,
    defaultValues: {
      name: '',
      description: '',
      unit: '',
    }
  });

  const visibleCategories = useMemo(() => {
    const businessTypes = selectedLocation?.business_types || [];
    const hasFish = businessTypes.includes('fish');
    const hasGoat = businessTypes.includes('goat');

    return categories.filter((category) => {
      if (category.id === 'other_items') return true;
      if (hasFish && fishCategoryIds.has(category.id)) return true;
      if (hasGoat && goatCategoryIds.has(category.id)) return true;
      return false;
    });
  }, [selectedLocation?.business_types]);

  useEffect(() => {
    if (visibleCategories.length === 0) return;
    const selectedStillVisible = visibleCategories.some((c) => c.id === selectedCategory);
    if (!selectedStillVisible) {
      setSelectedCategory(visibleCategories[0].id);
    }
  }, [visibleCategories, selectedCategory]);

  useEffect(() => {
    const selectedVisible = visibleCategories.some((c) => c.id === selectedCategory);
    if (selectedLocation?.id && selectedVisible) {
      loadMasterData(selectedCategory);
    } else {
      setMasterData([]);
    }
    // Clear editing state and reset form when category changes
    setEditingItem(null);
    setShowCreateForm(false);
    // Set default unit for feed_brands and goat_feed_brands when category changes
    const defaultValues = (selectedCategory === 'feed_brands' || selectedCategory === 'goat_feed_brands')
      ? { name: '', description: '', unit: 'kg', market_price_per_kg: undefined }
      : selectedCategory === 'fish_species'
      ? { name: '', description: '', unit: '', market_price_per_kg: undefined }
      : { name: '', description: '', unit: '', market_price_per_kg: undefined };
    form.reset(defaultValues);
  }, [selectedCategory, selectedLocation?.id, visibleCategories]);

  const extractErrorMessage = (err: any): string => {
    const detail = err?.response?.data?.detail;
    if (!detail) {
      return err?.message || 'Something went wrong';
    }
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      // FastAPI/Pydantic validation errors: [{loc, msg, type, ...}, ...]
      return detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ');
    }
    if (typeof detail === 'object') {
      return detail.msg || JSON.stringify(detail);
    }
    return String(detail);
  };

  const loadMasterData = async (category: string) => {
    if (!selectedLocation?.id) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get(`/master-data/${category}`, {
        params: { location_id: selectedLocation.id },
      });
      setMasterData(response.data);
    } catch (err: any) {
      setError(extractErrorMessage(err) || 'Failed to load master data');
      setMasterData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (data: MasterDataFormData) => {
    if (!selectedLocation?.id) {
      setError('Please select a farm location before adding master data.');
      return;
    }
    try {
      const itemData: any = {
        name: data.name,
        description: data.description,
        unit: data.unit,
        category: selectedCategory,
        location_id: selectedLocation.id,
      };
      // Only include market_price_per_kg for fish_species
      if (selectedCategory === 'fish_species') {
        itemData.market_price_per_kg = data.market_price_per_kg;
      }
      const newItem = await createMasterData('/master-data', itemData);
      if (newItem) {
        setMasterData([...masterData, newItem]);
        setShowCreateForm(false);
        form.reset();
      }
    } catch (err: any) {
      setError(extractErrorMessage(err));
    }
  };

  const handleEditItem = (item: MasterData) => {
    setEditingItem(item);
    // Normalize unit to lowercase to match select option values
    // Handle common variations: "GM" -> "g", "ML" -> "ml", etc.
    const normalizeUnit = (unit: string | null | undefined): string => {
      if (!unit) return '';
      const normalized = unit.toLowerCase().trim();
      // Map common variations to standard values
      const unitMap: Record<string, string> = {
        'gm': 'g',
        'gram': 'g',
        'grams': 'g',
        'milliliter': 'ml',
        'milliliters': 'ml',
        'millilitre': 'ml',
        'millilitres': 'ml',
        'tablet': 'tablets',
        'kilogram': 'kg',
        'kilograms': 'kg',
        'kilo': 'kg',
      };
      return unitMap[normalized] || normalized;
    };
    
    const normalizedUnit = normalizeUnit(item.unit);
    
    const formData = {
      name: item.name,
      description: item.description || '',
      unit: normalizedUnit,
      // Only include market_price_per_kg for fish_species
      market_price_per_kg: selectedCategory === 'fish_species' ? (item.market_price_per_kg || undefined) : undefined,
    };
    form.reset(formData);
    // Explicitly set unit value to ensure select dropdown reflects it correctly
    form.setValue('unit', normalizedUnit, { shouldValidate: false, shouldDirty: false });
    setShowCreateForm(true);
  };

  const handleUpdateItem = async (data: MasterDataFormData) => {
    if (!editingItem) return;
  if (!selectedLocation?.id) {
    setError('Please select a farm location before updating master data.');
    return;
  }
    
    try {
      const itemData: any = {
        name: data.name,
        description: data.description,
        unit: data.unit,
        category: selectedCategory,
        // location_id is immutable; backend enforces this
      };
      // Only include market_price_per_kg for fish_species
      if (selectedCategory === 'fish_species') {
        itemData.market_price_per_kg = data.market_price_per_kg;
      }
      const response = await apiClient.put(`/master-data/${editingItem.id}`, itemData);
      setMasterData(masterData.map(item => 
        item.id === editingItem.id ? response.data : item
      ));
      setShowCreateForm(false);
      setEditingItem(null);
      form.reset();
    } catch (err: any) {
      setError(extractErrorMessage(err) || 'Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      await apiClient.delete(`/master-data/${itemId}`);
      setMasterData(masterData.filter(item => item.id !== itemId));
    } catch (err: any) {
      setError(extractErrorMessage(err) || 'Failed to delete item');
    }
  };

  const currentCategory = visibleCategories.find(cat => cat.id === selectedCategory);

  if (!selectedLocation?.id) {
    return (
      <div className="container-mobile py-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Master Data Management</h1>
        <p className="text-gray-600 mb-4">
          Please select a farm location first. Master data (species, feeds, medicines) is specific to each farm.
        </p>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Master Data Management</h1>
        <p className="text-gray-600 mt-1">Configure fish species, goat breeds, medicines, and feed types</p>
      </div>

      {/* Category Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Category</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`p-4 text-left rounded-lg border-2 transition-colors ${
                selectedCategory === category.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{category.icon}</span>
                <div>
                  <h3 className="font-medium text-gray-900">{category.name}</h3>
                  <p className="text-sm text-gray-600">{category.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Current Category */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-4">
          <div className="flex items-center space-x-3 min-w-0">
            <span className="text-2xl">{currentCategory?.icon}</span>
            <h2 className="text-lg font-semibold text-gray-900 break-words">{currentCategory?.name}</h2>
          </div>
          <button
            onClick={() => {
              setEditingItem(null);
              // Set default unit for feed_brands and goat_feed_brands
              const defaultValues = (selectedCategory === 'feed_brands' || selectedCategory === 'goat_feed_brands')
                ? { name: '', description: '', unit: 'kg', market_price_per_kg: undefined }
                : selectedCategory === 'fish_species'
                ? { name: '', description: '', unit: '', market_price_per_kg: undefined }
                : { name: '', description: '', unit: '', market_price_per_kg: undefined };
              form.reset(defaultValues);
              setShowCreateForm(true);
            }}
            className="flex items-center justify-center sm:justify-start px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Add Item
          </button>
        </div>

        {/* Create/Edit Form */}
        {showCreateForm && (
          <div className="border-t border-gray-200 pt-6 mt-6">
            <h3 className="text-md font-medium text-gray-900 mb-4">
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </h3>
            
            <form 
              onSubmit={form.handleSubmit(editingItem ? handleUpdateItem : handleCreateItem)}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    {...form.register('name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={
                      selectedCategory === 'fish_species' ? 'e.g., Rohu, Catla, Pangas' :
                      selectedCategory === 'goat_breeds' ? 'e.g., Boer, Nubian, Jamnapari' :
                      selectedCategory === 'medicine_types' ? 'e.g., Antibiotic, Probiotic' :
                      selectedCategory === 'goat_medicine_types' ? 'e.g., Antibiotic, Probiotic' :
                      selectedCategory === 'feed_brands' ? 'e.g., Fish Feed Brand Name' :
                      selectedCategory === 'goat_feed_brands' ? 'e.g., Goat Feed Brand Name' :
                      'e.g., Diesel, Salt'
                    }
                  />
                  {form.formState.errors.name && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.name.message}</p>
                  )}
                </div>

                {(selectedCategory === 'medicine_types' || selectedCategory === 'goat_medicine_types' || selectedCategory === 'other_items' || selectedCategory === 'feed_brands' || selectedCategory === 'goat_feed_brands') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Unit
                    </label>
                    <select
                      {...form.register('unit')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Unit</option>
                      {(selectedCategory === 'feed_brands' || selectedCategory === 'goat_feed_brands') ? (
                        <option value="kg">Kilograms (kg)</option>
                      ) : selectedCategory === 'goat_medicine_types' ? (
                        <>
                          <option value="bottle">Bottle</option>
                          <option value="ml">Milliliters (ml)</option>
                          <option value="g">Grams (g)</option>
                          <option value="tablets">Tablets</option>
                          <option value="kg">Kilograms (kg)</option>
                        </>
                      ) : (
                        <>
                          <option value="ml">Milliliters (ml)</option>
                          <option value="g">Grams (g)</option>
                          <option value="tablets">Tablets</option>
                          <option value="kg">Kilograms (kg)</option>
                        </>
                      )}
                    </select>
                  </div>
                )}
              </div>

              {selectedCategory === 'fish_species' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Market Price per kg (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    {...form.register('market_price_per_kg')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter current market price per kg"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Current market price for this fish species (used for revenue projections)
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  {...form.register('description')}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Additional details about this item"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingItem(null);
                    form.reset();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center"
                >
                  {creating ? <LoadingSpinner size="sm" /> : (editingItem ? 'Update Item' : 'Add Item')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Items List */}
        <div className="mt-6">
          {loading ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : masterData.length > 0 ? (
            <div className="space-y-2">
              {masterData.map((item) => (
                <div key={item.id} className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 break-words">{item.name}</h4>
                    {item.description && (
                      <p className="text-sm text-gray-600 mt-1 break-words">{item.description}</p>
                    )}
                    <div className="flex items-center flex-wrap gap-2 mt-1">
                      {item.unit && (
                        <span className="inline-block px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">
                          {item.unit}
                        </span>
                      )}
                      {selectedCategory === 'fish_species' && item.market_price_per_kg && (
                        <span className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded font-medium">
                          ₹{item.market_price_per_kg}/kg
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center flex-wrap gap-2 sm:justify-end">
                    <button
                      onClick={() => handleEditItem(item)}
                      className="p-2 rounded-md border border-gray-200 text-gray-500 hover:text-blue-600 hover:border-blue-200"
                      aria-label="Edit item"
                      title="Edit item"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item.id)}
                      className="p-2 rounded-md border border-gray-200 text-gray-500 hover:text-red-600 hover:border-red-200"
                      aria-label="Delete item"
                      title="Delete item"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CogIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-md font-medium text-gray-900 mb-2">No {currentCategory?.name} Added</h3>
              <p className="text-gray-600 text-sm mb-4">Add your first {currentCategory?.name.toLowerCase()} to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MasterDataManagement;